/**
 * The small pieces every screen shares, lifted from the prototype so the
 * screens agree without each re-deriving them: a Phosphor glyph, the mono
 * eyebrow label, the menu surface and its rows, a square icon control, and a
 * popover that closes on an outside click or Escape.
 *
 * Larger components (Button, Switch, Dialog, Wordmark) come from the design
 * system bundle through `ds()`.
 */

import React, { useEffect, useRef } from 'react';

/** A Phosphor glyph. `name` without the `ph-` prefix. */
export function Ph({
  name,
  size = 16,
  fill = false,
  color,
  style,
  className
}: {
  name: string;
  size?: number;
  fill?: boolean;
  color?: string;
  style?: React.CSSProperties;
  className?: string;
}) {
  return (
    <i
      aria-hidden="true"
      className={`${fill ? 'ph-fill' : 'ph'} ph-${name}${className ? ` ${className}` : ''}`}
      style={{ fontSize: size, lineHeight: 1, ...(color ? { color } : {}), ...style }}
    />
  );
}

/** The JetBrains Mono uppercase eyebrow. */
export function MonoLabel({
  children,
  style
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div className="mono-label" style={style}>
      {children}
    </div>
  );
}

/** A square icon control from the prototype's chrome: 28–36px, muted at rest,
 *  a violet wash on hover. */
export function IconTap({
  icon,
  label,
  onClick,
  size = 32,
  glyph = 16,
  active = false,
  disabled = false,
  className,
  style
}: {
  icon: string;
  label: string;
  onClick?: (e: React.MouseEvent) => void;
  size?: number;
  glyph?: number;
  active?: boolean;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <button
      type="button"
      className={`bare hov-soft${className ? ` ${className}` : ''}`}
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      style={{
        width: size,
        height: size,
        display: 'grid',
        placeItems: 'center',
        borderRadius: 'var(--radius-chrome)',
        color: active ? 'var(--primary)' : 'var(--text-muted)',
        background: active ? 'var(--primary-soft)' : 'transparent',
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        flex: 'none',
        ...style
      }}
    >
      <Ph name={icon} size={glyph} />
    </button>
  );
}

/**
 * The menu surface: a card with a strong hairline, 5px padding and a large
 * shadow. Position it with `style` (the prototype anchors menus above the
 * composer with `bottom: calc(100% + 6px)`).
 */
export const Menu = React.forwardRef<
  HTMLDivElement,
  { children: React.ReactNode; style?: React.CSSProperties; role?: string }
>(function Menu({ children, style, role = 'menu' }, ref) {
  return (
    <div
      ref={ref}
      role={role}
      className="fade-in"
      style={{
        position: 'absolute',
        zIndex: 10,
        background: 'var(--surface-card)',
        border: '1px solid var(--border-strong)',
        borderRadius: 'var(--radius-app-card)',
        boxShadow: 'var(--shadow-lg)',
        padding: 5,
        boxSizing: 'border-box',
        ...style
      }}
    >
      {children}
    </div>
  );
});

/** One row of a menu. Neutral hover tint, never violet. */
export function MenuItem({
  icon,
  label,
  description,
  meta,
  selected = false,
  onSelect,
  iconColor,
  style
}: {
  icon?: string;
  label: React.ReactNode;
  description?: React.ReactNode;
  meta?: React.ReactNode;
  selected?: boolean;
  onSelect: () => void;
  iconColor?: string;
  style?: React.CSSProperties;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      className="bare hov-row"
      onClick={onSelect}
      style={{
        width: '100%',
        minHeight: description ? undefined : 34,
        display: 'flex',
        alignItems: description ? 'flex-start' : 'center',
        gap: 10,
        padding: description ? '7px 9px' : '0 9px',
        borderRadius: 6,
        fontSize: 13,
        color: 'var(--text-strong)',
        background: selected ? 'var(--border-subtle)' : 'transparent',
        ...style
      }}
    >
      {icon ? (
        <Ph
          name={icon}
          size={15}
          color={iconColor ?? 'var(--text-muted)'}
          style={description ? { marginTop: 2 } : undefined}
        />
      ) : null}
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
        {description ? (
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{description}</span>
        ) : null}
      </span>
      {meta ? <span style={{ fontSize: 11.5, color: 'var(--text-faint)' }}>{meta}</span> : null}
      {selected ? <Ph name="check" size={14} color="var(--primary)" /> : null}
    </button>
  );
}

export function MenuDivider() {
  return <div style={{ height: 1, background: 'var(--border-subtle)', margin: '5px 4px' }} />;
}

/**
 * Close something when the pointer goes down outside `ref`, or on Escape.
 * `active` so a closed menu is not listening.
 */
export function useDismiss(
  ref: React.RefObject<HTMLElement | null>,
  active: boolean,
  onDismiss: () => void
) {
  const cb = useRef(onDismiss);
  cb.current = onDismiss;

  useEffect(() => {
    if (!active) return;
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) cb.current();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cb.current();
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [ref, active]);
}

/** A thin progress bar, the prototype's 4px pill. */
export function Bar({ pct, style }: { pct: number; style?: React.CSSProperties }) {
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div
      style={{
        height: 4,
        borderRadius: 999,
        background: 'var(--app-panel-sunken)',
        overflow: 'hidden',
        ...style
      }}
    >
      <div style={{ width: `${clamped}%`, height: '100%', borderRadius: 999, background: 'var(--primary)' }} />
    </div>
  );
}

/** Project colours are token names on the wire; these are the tokens. */
export const PROJECT_COLOURS: Record<string, { dot: string; soft: string }> = {
  violet: { dot: 'var(--primary)', soft: 'var(--primary-soft)' },
  mauve: { dot: 'var(--accent)', soft: 'var(--accent-soft)' },
  teal: { dot: 'var(--signal)', soft: 'var(--signal-soft)' },
  slate: { dot: 'var(--text-muted)', soft: 'var(--app-panel-sunken)' }
};
